import type { CellCoordinate } from "@/interfaces/estimate-detail";
import { getStorageFileUrl } from "@/util/get-storage-file-url";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

export const ExcelCellHighlighter = ({ filePath, cellsToHighlight }: { filePath: string; cellsToHighlight?: CellCoordinate[] }) => {
  const [selectedSheet, setSelectedSheet] = useState<XLSX.WorkSheet | undefined>();
  const [sheetData, setSheetData] = useState<string[][]>([]);

  useEffect(() => {
    const loadFileAndSaveToState = async (filePath: string) => {
      const fullFilePath = getStorageFileUrl(filePath);
      const response = await fetch(fullFilePath);

      if (!response?.ok) {
        toast.error(`Failed to load .xlsx file`);
      }
      const arrayBuffer = await response.arrayBuffer();
      const data = new Uint8Array(arrayBuffer);
      const wb = XLSX.read(data, { type: "array" });

      if (wb.SheetNames.length > 0) {
        setSelectedSheet(wb.Sheets[wb.SheetNames[0]]);
      }
    };
    loadFileAndSaveToState(filePath);
  }, [filePath]);

  // load xlsx data as a json object, then filter out cells we don't want to display, then set state
  // with data we want to display
  useEffect(() => {
    if (selectedSheet === undefined) {
      return;
    }
    const jsonData = XLSX.utils.sheet_to_json(selectedSheet, {
      header: 1,
      defval: "",
      raw: false,
    }) as string[][];

    let lastRowWithData = -1;
    for (let i = jsonData.length - 1; i >= 0; i--) {
      const row = jsonData[i];
      const hasData = row.some((cell) => cell !== "" && cell !== null && cell !== undefined);
      if (hasData) {
        lastRowWithData = i;
        break;
      }
    }
    const trimmedData = lastRowWithData >= 0 ? jsonData.slice(0, lastRowWithData + 1) : [];
    setSheetData(trimmedData);
  }, [selectedSheet]);

  const maxCols = sheetData.reduce((max, row) => Math.max(max, row.length), 0);

  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to first highlighted cell when cellsToHighlight changes
  useEffect(() => {
    if (sheetData?.length && cellsToHighlight?.length) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        const firstHighlightedCell = containerRef.current?.querySelector(".highlighted-cell");
        if (firstHighlightedCell) {
          firstHighlightedCell.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [cellsToHighlight, sheetData]);

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div ref={containerRef} className="overflow-auto rounded-lg border border-gray-300">
        <table className="min-w-full divide-y divide-gray-200">
          <tbody className="divide-y divide-gray-200 bg-white">
            {sheetData.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {Array.from({ length: maxCols }, (_, colIndex) => {
                  const isHighlighted = (cellsToHighlight || []).some(
                    (cellsToHighlight) => cellsToHighlight.col === colIndex && cellsToHighlight.row === rowIndex,
                  );
                  const cellValue = row[colIndex] || "";

                  return (
                    <td
                      key={colIndex}
                      className={`border-r border-gray-200 px-3 py-2 text-sm whitespace-nowrap text-gray-900 ${
                        isHighlighted ? "highlighted-cell border-2 border-yellow-400 bg-yellow-200" : ""
                      }`}
                    >
                      {cellValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
